// SimulationChart.tsx — the per-scenario time-series chart: Marktwert, Anschaffungskosten
// (basis), Verrechnungskonto (settlement — can go negative, D-004). Draws a zero line and,
// when a crossover date exists, a vertical break-even marker. Max 3 series (exactly 3 here).
import { useCallback, useMemo, useState } from 'preact/hooks';
import type { Options } from 'uplot';
import type { SeriesPoint } from '@etf-cockpit/engine';
import { UplotBase } from './UplotBase.js';
import { formatCents, formatCentsCompact, formatDateDe } from '../lib/format.js';
import { zeroLinePlugin, verticalMarkerPlugin, verticalMarkersPlugin, chartZoomPlugin } from '../lib/uplot-plugins.js';
import type { ZoomState } from '../lib/uplot-plugins.js';
import { measuredAxisSize } from '../lib/uplotAxis.js';
import { hexToRgba, readCssVar } from '../lib/theme.js';
import { isSeriesVisible, toggleSeriesVisibility } from '../lib/seriesVisibility.js';
import type { SeriesVisibility } from '../lib/seriesVisibility.js';
import { Hint } from './Hint.js';

export interface SimulationChartProps {
  series: SeriesPoint[];
  crossoverDate?: string | null;
  /** Sonderzahlungen (one-off extra contribution) dates — drawn as small dotted markers,
   * distinct from the break-even line. Optional/cheap: omit for no markers at all. */
  extraContributionDates?: string[];
}

// Palette (see src/styles/global.css): Marktwert follows the current --accent theme
// preset (lib/theme.ts) so it recolors with Einstellungen -> Darstellung; Anschaffungskosten
// stays --warn, Verrechnungskonto stays --text-muted (neither is theme-able, per spec).
const COLOR_BASIS = '#fbbf24';
const COLOR_SETTLEMENT = '#93a0b8';

function toUnixSeconds(iso: string): number {
  return Math.floor(new Date(`${iso}T00:00:00Z`).getTime() / 1000);
}

/** Fixed key/label/color triples for the 3 series, reused for both the uPlot series
 * config and the toggle-chip row below the chart — one source of truth for "which series
 * exist" instead of the two lists silently drifting apart. */
const SERIES_DEFS = [
  { key: 'value', label: 'Marktwert' },
  { key: 'basis', label: 'Anschaffungskosten' },
  { key: 'settlement', label: 'Verrechnungskonto' },
] as const;

export function SimulationChart({ series, crossoverDate, extraContributionDates }: SimulationChartProps) {
  const hasNegativeSettlement = series.some((p) => p.settlementCents < 0);
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  // Interactive legend (task: "line toggles" on the Verlauf chart) — explicit Preact
  // state driven by lib/seriesVisibility.ts's pure toggle helper (rather than relying on
  // uPlot's own built-in legend-click handling) so the on/off logic is unit-testable
  // without a DOM/canvas, and so the toggle affordance is a plain, discoverable chip row
  // rather than an undocumented click-to-isolate gesture on the value legend.
  const [visibility, setVisibility] = useState<SeriesVisibility>({});

  const data = useMemo((): [number[], number[], number[], number[]] => {
    const x = series.map((p) => toUnixSeconds(p.date));
    const value = series.map((p) => p.valueCents);
    const basis = series.map((p) => p.basisCents);
    const settlement = series.map((p) => p.settlementCents);
    return [x, value, basis, settlement];
  }, [series]);

  const crossoverX = crossoverDate ? toUnixSeconds(crossoverDate) : null;
  const extraX = useMemo(
    () => (extraContributionDates ?? []).map(toUnixSeconds),
    [extraContributionDates],
  );

  const buildOptions = useCallback(
    (width: number): Options => {
      const colorValue = readCssVar('--accent', '#60a5fa');
      return {
        width,
        height: 220,
        padding: [8, 8, 0, 0],
        legend: { show: true },
        cursor: {
          drag: { x: true, y: true },
          points: { size: 6 },
        },
        scales: {
          x: { time: true },
          y: {
            range: (_u, initMin, initMax) => [Math.min(0, initMin), Math.max(0, initMax)],
          },
        },
        axes: [
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
          },
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
            values: (_u, ticks) => ticks.map((t) => formatCentsCompact(t)),
            size: measuredAxisSize,
          },
        ],
        series: [
          { label: 'Datum' },
          {
            label: 'Marktwert',
            stroke: colorValue,
            width: 2,
            fill: hexToRgba(colorValue, 0.08),
            show: isSeriesVisible(visibility, 'value'),
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
          {
            label: 'Anschaffungskosten',
            stroke: COLOR_BASIS,
            width: 1.5,
            dash: [4, 3],
            show: isSeriesVisible(visibility, 'basis'),
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
          {
            label: 'Verrechnungskonto',
            stroke: COLOR_SETTLEMENT,
            width: 1.5,
            show: isSeriesVisible(visibility, 'settlement'),
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
        ],
        plugins: [
          zeroLinePlugin(),
          verticalMarkerPlugin(() => crossoverX),
          verticalMarkersPlugin(() => extraX),
          chartZoomPlugin(setZoom),
        ],
      };
    },
    // visibility is included here (not just fed into a live setSeries call) so toggling
    // a chip re-instantiates the chart with the new show/hide config — simple and
    // correct; these charts are small enough that a full rebuild per toggle click is
    // imperceptible, same trade-off UplotBase already makes for crossoverX/extraX above.
    [crossoverX, extraX, setZoom, visibility],
  );

  if (series.length === 0) {
    return <div class="empty-state">Keine Zeitreihe vorhanden.</div>;
  }

  const swatchColors: Record<(typeof SERIES_DEFS)[number]['key'], string> = {
    value: readCssVar('--accent', '#60a5fa'),
    basis: COLOR_BASIS,
    settlement: COLOR_SETTLEMENT,
  };

  return (
    <div class="chart-wrap">
      <div class="chart-legend" role="group" aria-label="Serien ein-/ausblenden">
        {SERIES_DEFS.map((def) => (
          <button
            key={def.key}
            type="button"
            class="legend-toggle"
            aria-pressed={isSeriesVisible(visibility, def.key)}
            onClick={() => setVisibility((v) => toggleSeriesVisibility(v, def.key))}
          >
            <span class="swatch" style={{ background: swatchColors[def.key] }} />
            {def.label}
          </button>
        ))}
      </div>
      <UplotBase data={data} buildOptions={buildOptions} height={220} />
      {zoom?.zoomed ? (
        <div class="chart-zoom-row">
          <button type="button" class="chart-zoom-reset" onClick={() => zoom.reset()}>
            Zoom zurücksetzen
          </button>
        </div>
      ) : null}
      {crossoverDate ? (
        <Hint>
          <p class="field-hint">Break-even (gestrichelt): {formatDateDe(crossoverDate)}</p>
        </Hint>
      ) : null}
      {extraX.length > 0 ? (
        <Hint>
          <p class="field-hint">Punktierte Linien: Sonderzahlungen ({extraX.length}).</p>
        </Hint>
      ) : null}
      {hasNegativeSettlement ? (
        <div class="banner banner-neutral" role="status">
          <strong>KESt-Abbuchung</strong>
          <span>
            das Verrechnungskonto kann negativ werden, das ist korrekt so — die
            Kapitalertragsteuer wird direkt vom Konto abgebucht.
          </span>
        </div>
      ) : null}
    </div>
  );
}
